import {
  alignmentPlugin,
  dirtyPlugin,
  FabricEditor,
  FabricEditorProvider,
  fillGradientPlugin,
  fontLoadingPlugin,
  gridPlugin,
  historyPlugin,
  hotkeyPlugin,
  layerPlugin,
  rectCornersPlugin,
  resizeNormalizePlugin,
  selectionPlugin,
  zoomPlugin,
} from 'modules/fabric-editor';
import { Trans } from '@lingui/react/macro';
import { layoutBlocksPlugin } from 'modules/manager/domain/layout-blocks';
import { logoPlugin } from 'modules/manager/domain/logo';
import { placeholderPlugin } from 'modules/manager/domain/placeholder';
import {
  EditorCanvas,
  EditorHeader,
  EditorLayout,
  EditorLeftToolbar,
  EditorRightToolbar,
  UnsavedChangesGuard,
} from 'modules/manager/ui/components/editor';
import { EditorCanvasSkeleton, EditorLeftToolbarSkeleton, EditorRightToolbarSkeleton } from 'modules/manager/ui/components/skeletons';
import { StyleClipboardProvider, useLoadTemplate, useRestoreTemplate, useStyleClipboardHotkeys } from 'modules/manager/ui/hooks';
import { selectActiveTemplate } from 'modules/manager/store';
import React, { useEffect, useState } from 'react';
import { useAppSelector } from 'store/hooks';
import { UDButton, UDText } from 'ud-ui/components';

const PLUGINS = [
  historyPlugin(),
  zoomPlugin(),
  selectionPlugin(),
  alignmentPlugin(),
  layerPlugin(),
  gridPlugin(),
  hotkeyPlugin(),
  dirtyPlugin(),
  resizeNormalizePlugin(),
  rectCornersPlugin(),
  fillGradientPlugin(),
  fontLoadingPlugin(),
  placeholderPlugin(),
  layoutBlocksPlugin(),
  logoPlugin(),
];

const MediaManagerEditorPage: React.FC = () => {
  const { isLoaded, isLoading, error, reload } = useLoadTemplate();
  const [editor, setEditor] = useState<FabricEditor | null>(null);
  const template = useAppSelector(selectActiveTemplate);
  const { isRestored } = useRestoreTemplate(editor);

  // Готово — когда объекты шаблона легли в канву, а не когда пришёл ответ: между ними
  // канва пустая, и правка «пустого шаблона» потом затёрлась бы восстановлением.
  const isReady = editor != null && isLoaded && isRestored;
  // `error` в слайсе общий с сохранением — ошибкой загрузки он считается, только пока шаблона нет.
  const isLoadFailed = !isLoaded && !isLoading && error != null;

  useEffect(() => {
    if (!editor?.placeholders || !template) return;
    editor.placeholders.setTemplateType?.(template.templateType);
  }, [editor, template?.templateType]);

  useEffect(() => {
    if (!editor?.hotkeys || !editor.layers) return;
    const h = editor.hotkeys;
    const l = editor.layers;
    const bindings: Array<[string, () => void]> = [
      ['ctrl+]', () => l.bringForward()],
      ['meta+]', () => l.bringForward()],
      ['ctrl+shift+]', () => l.bringToFront()],
      ['meta+shift+]', () => l.bringToFront()],
      ['ctrl+[', () => l.sendBackwards()],
      ['meta+[', () => l.sendBackwards()],
      ['ctrl+shift+[', () => l.sendToBack()],
      ['meta+shift+[', () => l.sendToBack()],
    ];
    const a = editor.alignment;
    if (a) {
      bindings.push(
        ['alt+a', () => a.alignLeft()],
        ['alt+d', () => a.alignRight()],
        ['alt+w', () => a.alignTop()],
        ['alt+s', () => a.alignBottom()],
        ['alt+h', () => a.distributeHorizontally()],
        ['alt+v', () => a.distributeVertically()],
      );
    }
    const g = editor.grid;
    if (g) {
      bindings.push(['alt+g', () => g.fitToGrid()]);
    }
    for (const [combo, fn] of bindings) h.bind(combo, fn);
    return () => {
      for (const [combo] of bindings) h.unbind(combo);
    };
  }, [editor]);

  return (
    <FabricEditorProvider editor={editor}>
      <StyleClipboardProvider>
        <StyleClipboardHotkeys editor={editor} />
        <EditorLayout
          header={<EditorHeader />}
          // Скелетоны тех же ширин, а не пустота: слот канвы с первого кадра той же ширины,
          // и канва, смонтированная под скелетоном, не сдвигается при появлении тулбаров.
          left={isReady ? <EditorLeftToolbar /> : isLoadFailed ? null : <EditorLeftToolbarSkeleton />}
          right={isReady ? <EditorRightToolbar /> : isLoadFailed ? null : <EditorRightToolbarSkeleton />}
          canvas={<EditorCanvas plugins={PLUGINS} onReady={setEditor} />}
          canvasOverlay={
            isLoadFailed ? (
              <EditorLoadError onRetry={reload} />
            ) : isReady ? null : (
              <EditorCanvasSkeleton width={template?.width} height={template?.height} />
            )
          }
        />
        <UnsavedChangesGuard editor={editor} />
      </StyleClipboardProvider>
    </FabricEditorProvider>
  );
};

interface EditorLoadErrorProps {
  onRetry: () => void;
}

/** Ветка ошибки загрузки шаблона — в слоте канвы; шапка с «Назад» остаётся рабочей. */
const EditorLoadError: React.FC<EditorLoadErrorProps> = ({ onRetry }) => (
  <div className="flex flex-col items-center gap-2" role="alert">
    <UDText variant="body-2">
      <Trans>Не удалось загрузить шаблон</Trans>
    </UDText>
    <UDButton variant="secondary" onClick={onRetry}>
      <Trans>Повторить</Trans>
    </UDButton>
  </div>
);

interface StyleClipboardHotkeysProps {
  editor: FabricEditor | null;
}

// Render-less bridge: binds the style-clipboard hotkeys inside the provider so
// they share the clipboard context with the right-toolbar section.
const StyleClipboardHotkeys: React.FC<StyleClipboardHotkeysProps> = ({ editor }) => {
  useStyleClipboardHotkeys(editor);
  return null;
};

export default MediaManagerEditorPage;
